import type { MeContext } from "@/types/context";
import type { Portal } from "../portal";
import { getStoredUser } from "../auth";
import { get } from "./helpers";

/**
 * Fetch the portal "me / context" envelope (spec §C):
 *   GET /central/me  →  { central: { name, logo_url, plan }, user: { name, role, numerals }, flags: { search_disabled } }
 *   GET /admin/me    →  equivalent admin shape (no `central`).
 *
 * The response is normalized to the camelCase `MeContext` the UI consumes.
 *
 * RESILIENT BY DESIGN: this endpoint may not exist yet on the backend. On any
 * failure (404 wrong path, network/CORS, malformed body), we DON'T throw —
 * instead we synthesise a MeContext from the already-stored auth user
 * (`getStoredUser()`), so the sidebar brand block renders today using the
 * centre name + admin name we already have. 401/403 is the one case we let the
 * api interceptor handle (it clears the session and bounces to /login), so we
 * only swallow non-auth errors here.
 */
export async function fetchMe(portal: Portal): Promise<MeContext> {
  const path = portal === "admin" ? "/admin/me" : "/central/me";
  try {
    const body = await get<unknown>(path);
    return normalizeMe(body, portal);
  } catch {
    // Fallback: build context from the stored user so branding never flashes
    // broken while the /me endpoint is unavailable. The storybook decorator
    // seeds `sij_user` with `central_name`, so this path also drives the
    // synced preview/story.
    return fallbackMe(portal);
  }
}

/** Shape of the raw §C envelope (snake_case), unwrapped from `{ data: … }`. */
interface RawMe {
  central?: { name?: string; logo_url?: string; plan?: string } | null;
  user?: { name?: string; role?: string; numerals?: string } | null;
  flags?: { search_disabled?: boolean } | null;
}

function normalizeMe(body: unknown, portal: Portal): MeContext {
  // The API wraps payloads in `{ success, data }`; tolerate both wrapped and
  // bare shapes here rather than reusing unwrapItem (which throws on misses).
  const root = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  const raw = (
    "data" in root && root.data && typeof root.data === "object" ? root.data : root
  ) as RawMe;

  // If the envelope is unusable (no user name), degrade to the stored user.
  if (!raw.user?.name) return fallbackMe(portal);

  const role = raw.user.role === "admin" || raw.user.role === "central" ? raw.user.role : portal;

  return {
    central:
      portal === "central" && raw.central?.name
        ? {
            name: raw.central.name,
            logoUrl: raw.central.logo_url ?? undefined,
            plan: raw.central.plan ?? undefined,
          }
        : undefined,
    user: {
      name: raw.user.name,
      role,
      numerals: raw.user.numerals ?? undefined,
    },
    flags: raw.flags ? { searchDisabled: raw.flags.search_disabled ?? undefined } : undefined,
  };
}

/**
 * Build a MeContext from the stored auth user. Used whenever `/me` is missing
 * or unusable. Central name comes from the stored user's `central_name` (set at
 * login); admin name is the stored user's own `name`.
 */
function fallbackMe(portal: Portal): MeContext {
  const user = getStoredUser();
  return {
    central:
      portal === "central" && user?.central_name ? { name: user.central_name } : undefined,
    user: {
      name: user?.name ?? "",
      role: user?.role ?? portal,
    },
  };
}
